// Role/permission catalog. Authoritative values are loaded from seed at API start;
// this module provides constants, helpers, and the persona<->role mapping used by the UI shell.

export const ROLE_IDS = Object.freeze({
  EXECUTIVE_SPONSOR: 'ROLE-EXECUTIVE-SPONSOR',
  PORTFOLIO_MANAGER: 'ROLE-PORTFOLIO-MANAGER',
  PROGRAM_MANAGER: 'ROLE-PROGRAM-MANAGER',
  PROJECT_MANAGER: 'ROLE-PROJECT-MANAGER',
  FINANCIAL_ANALYST: 'ROLE-FINANCIAL-ANALYST',
  RESOURCE_MANAGER: 'ROLE-RESOURCE-MANAGER',
  SECURITY_COMPLIANCE_REVIEWER: 'ROLE-SECURITY-COMPLIANCE-REVIEWER',
  DATA_STEWARD: 'ROLE-DATA-STEWARD',
  AI_OPERATOR: 'ROLE-AI-OPERATOR',
  MARKET_RESEARCH_ANALYST: 'ROLE-MARKET-RESEARCH-ANALYST',
  CLINICAL_SME_REVIEWER: 'ROLE-CLINICAL-SME-REVIEWER',
  CONTRACTOR_ANALYST: 'ROLE-CONTRACTOR-ANALYST',
  READONLY_VIEWER: 'ROLE-READONLY-VIEWER',
  SYSTEM_ADMIN: 'ROLE-SYSTEM-ADMIN',
});

// User IDs verified against seed-data/users.seed.json.
export const PERSONA_TO_USER_ID = Object.freeze({
  executive_sponsor: 'USR-EXE-HAYES',
  delivery_leader: 'USR-PFM-RODRIGUEZ',
  data_steward: 'USR-DATA-KIM',
  pending_access: 'USR-CTR-VAUGHN',
  system_admin: 'USR-SYS-BAKER',
});

export const PERSONA_DEFAULT_ROUTE = Object.freeze({
  executive_sponsor: '/executive',
  delivery_leader: '/delivery',
  data_steward: '/admin/data',
  pending_access: '/access/status',
  system_admin: '/admin/rbac',
});

// `personName` is the fixed demo identity that seeds behind each persona (see
// PERSONA_TO_USER_ID). Surfaced on the login persona cards so an evaluator sees
// who they are signing in as. Additive only — existing label/summary/accent
// consumers are unaffected.
export const PERSONA_DISPLAY = Object.freeze({
  executive_sponsor: {
    label: 'Executive Sponsor',
    personName: 'Morgan Hayes',
    summary: 'Mission pulse, predictive risk, briefings, AI copilot.',
    accent: 'sponsor',
  },
  delivery_leader: {
    label: 'Delivery / Portfolio Leader',
    personName: 'Elena Rodriguez',
    summary: 'Sprint health, roadmap, gate readiness, AI risk runs.',
    accent: 'delivery',
  },
  data_steward: {
    label: 'Data Steward / Admin',
    personName: 'Jae Kim',
    summary: 'Connectors, lineage, access reviews, and reconciliation.',
    accent: 'steward',
  },
  pending_access: {
    label: 'Pending Access User',
    personName: 'Parker Vaughn',
    summary: 'eSAAR draft / submitted — limited surface until approval.',
    accent: 'pending',
  },
  system_admin: {
    label: 'System Admin',
    personName: 'Archie Bockhorst',
    summary: 'Audit-level inspection of access and workspace controls.',
    accent: 'system-admin',
  },
});

// Roles that may approve/reject weekly timesheets. Single source of truth for
// the API route guard (time.routes.js) and role-aware rendering in the web app.
export const TIME_APPROVER_ROLES = Object.freeze([
  ROLE_IDS.EXECUTIVE_SPONSOR,
  ROLE_IDS.PORTFOLIO_MANAGER,
  ROLE_IDS.PROGRAM_MANAGER,
  ROLE_IDS.RESOURCE_MANAGER,
  ROLE_IDS.FINANCIAL_ANALYST,
]);

// Change-control-board roles: the only roles that may decide (approve/reject/
// defer) a change request (AC-GOV-4.1). Project managers may raise and edit
// change requests but never decide their own — decisions sit with the board.
export const CHANGE_BOARD_ROLES = Object.freeze([
  ROLE_IDS.EXECUTIVE_SPONSOR,
  ROLE_IDS.PORTFOLIO_MANAGER,
  ROLE_IDS.PROGRAM_MANAGER,
  ROLE_IDS.FINANCIAL_ANALYST,
]);

/**
 * Resolve whether an actor (set of roles) holds any of the required roles.
 * SYSTEM_ADMIN always passes. Empty required set means "authenticated user is enough".
 */
export function hasAnyRole(actorRoles, requiredRoles) {
  if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) return true;
  const set = new Set(actorRoles || []);
  if (set.has(ROLE_IDS.SYSTEM_ADMIN)) return true;
  return requiredRoles.some((r) => set.has(r));
}

/**
 * Resolve whether an actor holds the named permission.
 * permissionsByRole — { [roleId]: Set<permissionId> } map, built from seed at API start.
 */
export function hasPermission(actorRoles, permissionId, permissionsByRole) {
  if (!permissionId) return true;
  const set = new Set(actorRoles || []);
  if (set.has(ROLE_IDS.SYSTEM_ADMIN)) return true;
  for (const role of set) {
    const perms = permissionsByRole?.get?.(role);
    if (perms?.has?.(permissionId)) return true;
  }
  return false;
}

/**
 * Route permission map — declarative gate evaluated by the AppShell + API alike.
 * Empty array = anyone authenticated. system_admin always granted.
 */
export const ROUTE_PERMISSIONS = Object.freeze({
  '/': [],
  '/login': [],
  '/change-password': [],
  '/register': [],
  '/profile/setup': [],
  '/access/request': [],
  '/access/status': [],
  '/home': [],
  '/me': [],
  '/settings': [],
  '/not-authorized': [],
  '/notifications': [],
  // Personal activity history (PRIME-ACT-UX-001). Empty list = any authenticated
  // persona, same contract as '/notifications'. Admin/investigator reporting
  // stays separately gated at '/admin/activity' and '/admin/audit'.
  '/activity': [],
  '/saved-views': [],
  // Giving feedback is for every authenticated persona (empty list = any
  // signed-in user); only the governed TRIAGE queue below stays role-gated.
  '/feedback': [],
  '/feedback/workspace': [],
  // Reporting a problem to the service desk is for every authenticated persona —
  // an access or sign-in problem is exactly the kind of ticket a role-less
  // Pending Access user needs to file. Row-level ownership in
  // feedbackIntakes.service.js is what keeps one reporter out of another's draft.
  '/feedback/support/new': [],
  '/feedback/support/:intakeId': [],
  // Asking the product team for a feature or a service is also for every
  // authenticated persona (commit 4). Row-level ownership in
  // feedbackIntakes.service.js is what keeps one reporter out of another's draft,
  // and the governed record it produces still lands in the System-Admin queue.
  '/feedback/request/new': [],
  '/feedback/request/:intakeId': [],
  // The governed triage queue (and its aggregate admin APIs) is System Admin
  // only — the API's reviewer checks already are, and granting the page to
  // management roles produced a reporter-scoped view with erroring triage
  // controls. hasAnyRole always passes SYSTEM_ADMIN, so this list is the gate
  // for everyone else.
  '/feedback/queue': [ROLE_IDS.SYSTEM_ADMIN],
  '/uat': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.DATA_STEWARD,
    ROLE_IDS.CLINICAL_SME_REVIEWER,
  ],

  '/executive': [ROLE_IDS.EXECUTIVE_SPONSOR, ROLE_IDS.PORTFOLIO_MANAGER, ROLE_IDS.PROGRAM_MANAGER],
  '/analytics/mission-impact': [ROLE_IDS.EXECUTIVE_SPONSOR, ROLE_IDS.PORTFOLIO_MANAGER],
  '/portfolios': [ROLE_IDS.EXECUTIVE_SPONSOR, ROLE_IDS.PORTFOLIO_MANAGER, ROLE_IDS.PROGRAM_MANAGER],
  '/programs': [ROLE_IDS.PORTFOLIO_MANAGER, ROLE_IDS.PROGRAM_MANAGER, ROLE_IDS.EXECUTIVE_SPONSOR],
  '/projects': [
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.READONLY_VIEWER,
  ],
  '/project-factory': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
  ],
  '/project-factory/review': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
  ],
  '/loe-dashboard': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
  ],
  '/analytics/loes': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
  ],
  '/actions': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
    ROLE_IDS.READONLY_VIEWER,
  ],
  '/meetings': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/knowledge': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.DATA_STEWARD,
  ],
  '/resources': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/analytics/resources': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/resources/scheduler': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/resources/utilization': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/resources/skills': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/resources/allocations/:id': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/resources/assignments/new': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
  ],
  '/timesheets': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
    ROLE_IDS.CONTRACTOR_ANALYST,
    ROLE_IDS.CLINICAL_SME_REVIEWER,
  ],
  '/timesheets/current': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.RESOURCE_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
    ROLE_IDS.CONTRACTOR_ANALYST,
    ROLE_IDS.CLINICAL_SME_REVIEWER,
  ],
  '/governance': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
  ],
  '/governance/risks': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
  ],
  '/governance/issues': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
  ],
  '/governance/changes': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
  ],
  '/governance/evm': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
  ],
  '/rapids': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.DATA_STEWARD,
    ROLE_IDS.SYSTEM_ADMIN,
  ],
  '/delivery': [ROLE_IDS.PORTFOLIO_MANAGER, ROLE_IDS.PROGRAM_MANAGER, ROLE_IDS.PROJECT_MANAGER],
  '/work-items/:id': [
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
  ],
  '/roadmap': [ROLE_IDS.PORTFOLIO_MANAGER, ROLE_IDS.PROGRAM_MANAGER, ROLE_IDS.PROJECT_MANAGER],
  '/financials': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
    ROLE_IDS.READONLY_VIEWER,
  ],
  '/financials/adjustments': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
  ],
  '/financials/funding-sources': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.FINANCIAL_ANALYST,
    ROLE_IDS.READONLY_VIEWER,
  ],
  '/lifecycle': [ROLE_IDS.EXECUTIVE_SPONSOR, ROLE_IDS.PORTFOLIO_MANAGER, ROLE_IDS.PROGRAM_MANAGER],
  '/decision-support': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
  ],
  '/market-research': [
    ROLE_IDS.MARKET_RESEARCH_ANALYST,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
  ],

  '/ai/orchestration': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.AI_OPERATOR,
    ROLE_IDS.SYSTEM_ADMIN,
  ],
  '/ai/ask': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.DATA_STEWARD,
    ROLE_IDS.AI_OPERATOR,
  ],

  '/admin/data': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/custom-fields': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/forms': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/rubrics': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/report-templates': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/config/report-registry': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/ingestion': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/lineage': [ROLE_IDS.DATA_STEWARD, ROLE_IDS.SYSTEM_ADMIN],
  '/admin/rbac': [ROLE_IDS.SYSTEM_ADMIN],
  '/admin/site-access': [ROLE_IDS.SYSTEM_ADMIN],
  '/admin/access-requests': [
    ROLE_IDS.SYSTEM_ADMIN,
    ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER,
    ROLE_IDS.DATA_STEWARD,
    ROLE_IDS.PORTFOLIO_MANAGER,
  ],
  '/admin/users': [ROLE_IDS.SYSTEM_ADMIN],
  '/admin/guide-results': [ROLE_IDS.SYSTEM_ADMIN],
  '/admin/authentication': [ROLE_IDS.SYSTEM_ADMIN],
  '/admin/audit': [ROLE_IDS.SYSTEM_ADMIN, ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER],
  '/admin/activity': [ROLE_IDS.SYSTEM_ADMIN, ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER],
  '/admin/activity/reports/:id': [ROLE_IDS.SYSTEM_ADMIN, ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER],
  '/admin/compliance': [ROLE_IDS.SYSTEM_ADMIN, ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER],
  '/products': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.DATA_STEWARD,
    ROLE_IDS.AI_OPERATOR,
    ROLE_IDS.READONLY_VIEWER,
    ROLE_IDS.SYSTEM_ADMIN,
  ],
  '/status-packs': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.SYSTEM_ADMIN,
  ],
  '/exports': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
    ROLE_IDS.DATA_STEWARD,
    ROLE_IDS.AI_OPERATOR,
    ROLE_IDS.READONLY_VIEWER,
    ROLE_IDS.SYSTEM_ADMIN,
  ],

  // Help & getting-started surfaces (SME onboarding). Empty array = any
  // authenticated user, same contract as '/home'.
  '/help': [],
  '/help/glossary': [],
  '/help/capability-map': [],
  // Typed help/intake framework, commit 5. Asking PRIME a question is open to
  // every authenticated persona, exactly like the other typed intakes — declared
  // EXPLICITLY rather than relying on '/help' prefix inheritance, which neither
  // the web guard nor requireRouteAccess performs.
  '/help/questions': [],

  // Project-factory request intake — the J1 entry point. Guarded with the same
  // delivery roles as '/project-factory' so the essential onboarding route is
  // RBAC-known rather than an implicit "any authenticated" gap.
  '/project-factory/requests/new': [
    ROLE_IDS.EXECUTIVE_SPONSOR,
    ROLE_IDS.PORTFOLIO_MANAGER,
    ROLE_IDS.PROGRAM_MANAGER,
    ROLE_IDS.PROJECT_MANAGER,
  ],
});

export function canSeeRoute(routePath, actorRoles) {
  const required = ROUTE_PERMISSIONS[routePath];
  if (required === undefined) return true;
  return hasAnyRole(actorRoles, required);
}

// Roles that may read governed AI runs. Derived from the AI surface route
// grants rather than hand-listed, so the API gate on /api/ai/runs and any other
// consumer of AI-run provenance cannot drift apart from the routes that produce
// those runs. `apps/api/src/routes/ai.routes.js` and the typed-intake
// AI-run linkage check both resolve their policy from here.
export const AI_SURFACE_ROUTE_KEYS = Object.freeze([
  '/ai/ask',
  '/ai/orchestration',
  '/decision-support',
]);

export function resolveAiSurfaceRoles() {
  return [...new Set(AI_SURFACE_ROUTE_KEYS.flatMap((key) => ROUTE_PERMISSIONS[key] || []))];
}
